import React, { useState, useRef } from 'react';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import PositionedModal from 'common/components/PositionedModal';
import { useToasts } from 'common/toast';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCurrentOrganizationId } from 'common/selectors/auth';
import { H4 } from 'common/styles/page';
import { useImportEmployeeRecordsMutation } from '../../../../features/employee/employeeApi';

interface ImportOrganizationMemberModalProps {
  open: boolean;
  onClose: () => void;
}

const ImportMemberRecordModal: React.FC<ImportOrganizationMemberModalProps> = ({
  open,
  onClose,
}) => {
  const { addToast } = useToasts();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const organizationId = useSelector(getCurrentOrganizationId);

  // Use the import mutation
  const [importRecords, { isLoading: isImporting }] = useImportEmployeeRecordsMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setErrors([]);
    }
  };

  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!selectedFile) {
      setErrors(['Please select a CSV file to import']);
      return;
    }

    setIsUploading(true);
    setErrors([]);

    try {
      // Create FormData object
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await importRecords({
        organizationId,
        fileUpload: formData as any,
      }).unwrap();

      // If result is an object, navigate to the result page and pass it in state
      if (result && typeof result === 'object') {
        // Navigate to the result viewer page and pass the response as state
        navigate('/app/team-management/import/result', { state: { result } });
        onClose();
        return;
      }

      // Fallback
      addToast('Member records imported successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
      onClose();
    } catch (error) {
      console.error('Failed to import member records:', error);

      // Handle different error formats
      if (error && typeof error === 'object') {
        if (
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'errors' in error.data
        ) {
          setErrors(error.data.errors as string[]);
        } else if ('message' in error) {
          setErrors([error.message as string]);
        } else {
          setErrors(['Failed to import member records. Please try again.']);
        }
      } else {
        setErrors(['Failed to import member records. Please try again.']);
      }

      addToast('Failed to import member records', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const isLoading = isUploading || isImporting;

  return (
    <PositionedModal open={open} onClose={onClose} size="small">
      <Modal.Header>Import Member Records</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <H4>
              <span style={{ color: 'green', fontWeight: 700 }}>IMPORT</span> new member records
            </H4>
          </Form.Field>
          <Form.Field>
            <FileUploadContainer>
              <FileInput
                id="member-record-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={isLoading || !organizationId}
              />
              <FileInputButton htmlFor="member-record-file" disabled={isLoading || !organizationId}>
                Choose file
              </FileInputButton>
              <FileName>{selectedFile?.name || 'No file selected'}</FileName>
            </FileUploadContainer>
          </Form.Field>

          {errors.length > 0 && (
            <Message negative>
              <Message.Header>Import Errors</Message.Header>
              <ErrorList>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ErrorList>
            </Message>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!selectedFile || !organizationId}
        >
          Import
        </Button>
      </Modal.Actions>
    </PositionedModal>
  );
};

const FileUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputButton = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  padding: 0.75rem 1.2rem;
  background: #2185d0;
  color: white;
  font-weight: 700;
  border-radius: 0.35rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  border: none;
  text-align: center;
  user-select: none;
`;

const FileName = styled.span`
  color: #333;
  font-size: 0.95rem;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 1.5em;
`;

export default ImportMemberRecordModal;
